import { AdEnginePlugin, type Hooks } from "../../../../core/AdEnginePlugin";
import type {
  NielsenInstance,
  NielsenPluginConfig,
  NielsenWindowConfig,
} from "./types";
import { initNielsenStaticQueue } from "./static-queue-scripts";
import { getBooleanQueryParam } from "utils";
import type { FandomCmp } from "common-types";
import type { GptWrapper } from "../../../../core/GptWrapper";
import { isConsentGranted } from "../../../../helpers/isConsentGranted";

/**
 * NielsenPlugin responsible for loading in the Nielsen SDK and initializing it.
 * This Plugin loads in the static-queue-scripts.ts file, which is included within the Odyssey code.
 *
 * Implementation documentation can be found online.
 * @see {@link https://engineeringportal.nielsen.com/wiki/DCR_Static_Browser_SDK_(6.0.0)}
 * */
export class NielsenPlugin extends AdEnginePlugin {
  /**
   * The unique Nielsen identifier for Fandom (API Key).
   */
  private readonly NIELSEN_KEY = "P26086A07-C7FB-4124-A679-8AC404198BA7";
  /**
   * Stores the configuration for loading the Nielsen script.
   */
  private readonly nielsenWindowConfig: NielsenWindowConfig = {};
  /**
   * Tracks whether the Nielsen script has been instantiated and loaded.
   */
  private isLoaded = false;

  /**
   * A reference to the GptWrapper instance, which provides targeting data for the page.
   * This property is optional and will be assigned when the `onAfterGptWrapperInit` hook is triggered.
   */
  private gptWrapper?: GptWrapper;

  /**
   * Stores the consent data required to initialize the Nielsen SDK.
   * */
  private consent = false;

  /**
   * Constructs a new instance of the NielsenPlugin.
   */
  constructor() {
    super();
  }

  /**
   * Checks if the Nielsen plugin is enabled based on the configuration.
   * This method is used to check whether the NielsenPlugin should be instantiated at all.
   *
   * @returns `true` if the plugin is enabled, otherwise `false`.
   */
  static isEnabled({ icNielsen }: NielsenPluginConfig): boolean {
    return !!icNielsen;
  }

  /**
   * Initializes the Nielsen static queue (inlined script from Nielsen)
   * and retrieves the Nielsen instance. An optional debug mode can be set,
   * when the query parameter `nielsen-dcr-debug` is set to `1`.
   *
   * @returns The Nielsen instance for further interactions.
   */
  private getNielsenInstance(): NielsenInstance {
    initNielsenStaticQueue();

    if (getBooleanQueryParam("nielsen-dcr-debug")) {
      this.nielsenWindowConfig.nol_sdkDebug = "debug";
    }

    // Get the nielsen instance from the nlsQ function, so that we can later call the internal ggPM function
    return window?.NOLBUNDLE.nlsQ(
      this.NIELSEN_KEY,
      "nlsnInstance",
      this.nielsenWindowConfig,
    );
  }

  /**
   * Initializes the Nielsen SDK if it is enabled and not already loaded.
   * Sets up metadata for the Nielsen instance and marks the plugin as loaded.
   */
  private init(): void {
    if (this.isLoaded || !this.gptWrapper) {
      return;
    }
    // Initialize Nielsen SDK or any other logic here
    const nielsenInstance = this.getNielsenInstance();

    // Section (s0v), ArticleId (on wikis), and wiki name
    /*section*/
    const s0v = this.gptWrapper.getTargeting("s0v")?.join(",");
    /*articleId on wikis*/
    const artid = this.gptWrapper.getTargeting("artid")?.join(",");
    /*wiki name*/
    const s1 = this.gptWrapper.getTargeting("s1")?.join(",");

    // Future note: artId may need to change when this is implemented in F2 (fandom.com homepage)
    // artid is replaced with post_id on the news and stories pages, which are fandom.com/articles/* pages.

    const metadata = {
      type: "static",
      assetid: `fandom.com/${s0v}/${s1}/${artid}`,
      section: `FANDOM ${s0v?.replace(/_/g, " ").toUpperCase()} NETWORK`,
    };

    nielsenInstance.ggPM("static", metadata);
    this.isLoaded = true;
  }

  hooks: Hooks = {
    onConsentUpdated: (fandomCmp: FandomCmp) => {
      this.consent = isConsentGranted(fandomCmp);
      if (!this.consent) {
        return;
      }
      this.init();
    },
    onAfterGptWrapperInit: (gptWrapper: GptWrapper) => {
      this.gptWrapper = gptWrapper;
      if (!this.consent) {
        return;
      }
      this.init();
    },
  };
}
